import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  DialogContent,
  Dialog,
  DialogTitle,
  DialogActions
} from "@mui/material";
import { db } from "../../../firebase"; // Adjust path as per your project structure
import { collection, setDoc, addDoc, Timestamp } from "firebase/firestore";

const AddTimetable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const [formData, setFormData] = useState({
    availableSlots: 0,
    clinicId: "clinic456",
    createdAt: new Date().toISOString(), // current timestamp
    date: "",
    day: "",
    doctorId: "", // Initialize with empty string
    firstHalf: {
      availableSlots: 0,
      timeEnd: "",
      timeStart: "",
    },
    secondHalf: {
      availableSlots: 0,
      timeEnd: "",
      timeStart: "",
    },
    status: "Live",
    note: "",
    timestamp: "", // Placeholder for timestamp field
  });

  useEffect(() => {
    // Extract the `doctorId` from the URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const doctorId = queryParams.get("doctorId");
    if (doctorId) {
      setFormData((prev) => ({ ...prev, doctorId }));
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleHalfChange = (half, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [half]: {
        ...prev[half],
        [field]: value,
      },
    }));
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const selectedDay = new Date(selectedDate).toLocaleString("en-us", {
      weekday: "long",
    });

    setFormData((prev) => ({
      ...prev,
      date: selectedDate,
      day: selectedDay,
    }));
  };

  const calculateTotalSlots = () => {
    const { firstHalf, secondHalf } = formData;
    return (
      parseInt(firstHalf.availableSlots || 0) +
      parseInt(secondHalf.availableSlots || 0)
    );
  };

  const handleAdd = async () => {
    try {
      const { date, firstHalf, secondHalf, note } = formData;
  
      // Convert the input date string (e.g., "2024-12-11") to a Date object
      const combinedDate = new Date(date);
      if (isNaN(combinedDate)) {
        console.error("Invalid date provided:", date);
        return; // Exit early to prevent further errors
      }
  
      combinedDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00
      const timestamp = combinedDate.getTime(); // This timestamp represents midnight of the selected day
  
      // Convert the first and second half time to timestamps as well
      const firstHalfStartDate = firstHalf.timeStart
        ? new Date(`${date}T${firstHalf.timeStart}:00`)
        : null;
      const firstHalfEndDate = firstHalf.timeEnd
        ? new Date(`${date}T${firstHalf.timeEnd}:00`)
        : null;
      const secondHalfStartDate = secondHalf.timeStart
        ? new Date(`${date}T${secondHalf.timeStart}:00`)
        : null;
      const secondHalfEndDate = secondHalf.timeEnd
        ? new Date(`${date}T${secondHalf.timeEnd}:00`)
        : null;
  
      // Store the document in Firestore with valid timestamps
      const updatedFormData = {
        ...formData,
        note, // Ensure that the `note` field is included
        timestamp: Timestamp.fromMillis(timestamp), // Store the Firestore Timestamp for backend use
        dateWithTimestamp: timestamp, // Store the timestamp of midnight
        firstHalfStart: firstHalfStartDate
          ? firstHalfStartDate.getTime()
          : null,
        firstHalfEnd: firstHalfEndDate ? firstHalfEndDate.getTime() : null,
        secondHalfStart: secondHalfStartDate
          ? secondHalfStartDate.getTime()
          : null,
        secondHalfEnd: secondHalfEndDate ? secondHalfEndDate.getTime() : null,
        availableSlots: calculateTotalSlots(),
      };
  
      console.log("Final Timestamp:", updatedFormData.timestamp);
  
      // Make sure 'note' is included in the Firestore document
      const docRef = await addDoc(collection(db, "timetable"), updatedFormData);
      await setDoc(docRef, { ...updatedFormData, id: docRef.id });
  
      console.log("Document added with ID:", docRef.id);
      navigate(-1); // Redirect to the timetable page
      setDialogMessage("Timetable successfully added!");
      setOpenDialog(true); // Open the dialog
    } catch (error) {
      console.error("Error adding document:", error);
    }
  };
  
  return (
    <Box sx={{ width: "80%", mx: "auto", my: 4 }}>
      <Typography variant="h4" gutterBottom>
        Add Timetable
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Available Slots"
            type="number"
            name="availableSlots"
            value={calculateTotalSlots()} // Calculate total slots here
            fullWidth
            margin="normal"
            disabled // Make it disabled as it is auto-generated
            InputProps={{
              readOnly: true, // Make the input readonly so the user can't edit directly
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Date"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleDateChange}
            fullWidth
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Day"
            value={formData.day}
            fullWidth
            margin="normal"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Half Available Slots"
            type="number"
            value={formData.firstHalf.availableSlots}
            onChange={(e) =>
              handleHalfChange("firstHalf", "availableSlots", e.target.value)
            }
            fullWidth
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Half Start Time"
            type="time"
            value={formData.firstHalf.timeStart}
            onChange={(e) =>
              handleHalfChange("firstHalf", "timeStart", e.target.value)
            }
            fullWidth
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Half End Time"
            type="time"
            value={formData.firstHalf.timeEnd}
            onChange={(e) =>
              handleHalfChange("firstHalf", "timeEnd", e.target.value)
            }
            fullWidth
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Second Half Available Slots"
            type="number"
            value={formData.secondHalf.availableSlots}
            onChange={(e) =>
              handleHalfChange("secondHalf", "availableSlots", e.target.value)
            }
            fullWidth
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Second Half Start Time"
            type="time"
            value={formData.secondHalf.timeStart}
            onChange={(e) =>
              handleHalfChange("secondHalf", "timeStart", e.target.value)
            }
            fullWidth
            margin="normal"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Second Half End Time"
            type="time"
            value={formData.secondHalf.timeEnd}
            onChange={(e) =>
              handleHalfChange("secondHalf", "timeEnd", e.target.value)
            }
            fullWidth
            margin="normal"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Note"
            value={formData.note}
            onChange={handleChange} // Directly pass handleChange
            fullWidth
            margin="normal"
            multiline
            rows={4}
            name="note" // Ensure it's correctly bound to state
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAdd}
            fullWidth
          >
            Add
          </Button>
        </Grid>
      </Grid>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{dialogMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddTimetable;
